import { Range } from 'rc-slider';
import 'rc-slider/assets/index.css';
import type { ReactNode } from 'react';
import type { DefaultTheme, StyledComponent } from 'styled-components';
import styled, { css, useTheme } from 'styled-components';
import { TypographyVariant } from '../../utils/theme';
import { Input } from '../input';
import Typography from '../typography/typography';

export interface IEmployerSlider {
  label?: string;
  employeeMaxPercentageContribution: number;
  employerMaxPercentageContribution: number;
  step: number;
  absoluteValueComponent?: ReactNode;
  value: number;
}

export function EmployerSlider({
  label = '',
  absoluteValueComponent,
  employeeMaxPercentageContribution,
  employerMaxPercentageContribution,
  step,
  value,
}: IEmployerSlider) {
  const theme = useTheme();
  return (
    <SliderContainer>
      <StyledTypography variant={TypographyVariant.bodyMd2}>
        {label ? `${label}` : '% Employer Contributes'}
      </StyledTypography>
      {typeof value === 'number' && (
        <StyledInput
          value={value}
          name={'value'}
          showLabel={false}
          disabled
          defaultValue={value.toString()}
          width="140px"
          height="38px"
        />
      )}
      <Range
        min={0}
        max={employeeMaxPercentageContribution}
        count={3}
        step={step || 0.5}
        defaultValue={[0, 0, 0, employerMaxPercentageContribution]}
        pushable
        disabled={true}
        trackStyle={[
          {
            background: `${theme.palette.primary.dark}`,
            zIndex: 3,
            height: '30px',
            borderRadius: '100px',
          },
          {
            background: `${theme.palette.neutral[50]}`,
            zIndex: 2,
            marginLeft: '-5px',
            height: '30px',
            borderRadius:
              value < 0.5 ? '100px 100px 100px 100px' : '0px 100px 100px 0px',
          },
          {
            background: `${theme.palette.neutral['00']}`,
            zIndex: 2,
            marginLeft: '-5px',
            height: '30px',
            borderTopRightRadius: '100px',
            borderBottomRightRadius: '100px',
          },
        ]}
        value={[
          0,
          value,
          employerMaxPercentageContribution,
          employeeMaxPercentageContribution,
        ]}
        handleStyle={[
          { display: 'none' },
          {
            display: 'none',
          },
          { display: 'none' },
          { display: 'none' },
        ]}
        railStyle={{
          backgroundColor: 'transparent',
        }}
        data-testid={`${
          label ? label.replaceAll(' ', '_') : ''
        }-employer-slider`}
      />
      {absoluteValueComponent}
    </SliderContainer>
  );
}

export default EmployerSlider;

const StyledTypography = styled(Typography)`
  ${({ theme }) => css`
  text-align: right;
  margin-right: ${theme.spacing(0.5)}};
  width: 100%;
  max-width: 90px;
  `}
`;

const StyledInput = styled(Input)`
  ${({ theme }) => css`
    ${theme.typography.bodyMd1};
  `}
`;

const SliderContainer: StyledComponent<
  'div',
  DefaultTheme,
  Record<string, unknown> & { link?: string },
  never
> = styled.div`
  ${({ theme }) => css`
    width: 100%;
    height: 30px;
    display: flex;
    gap: ${theme.spacing(3)};
    margin-bottom: ${theme.spacing(1)};
    justify-content: center;
    align-items: center;
    flex-direction: row;

    .rc-slider {
      height: 30px;
      padding: 0px;
      background: 'none';
    }

    .rc-slider-disabled {
      background: none;
    }
  `}
`;
